import { useState } from 'react'
import { get, patch } from '../../api'
import { Alert, Button, Empty, Field, Input, Modal, Spinner, Textarea } from '../../components/ui'
import { useAction, useDebounced, useLoad } from '../../hooks'
import type { Booking, Guest } from '../../types'
import { BookingRow } from './Today'
import { BookingDetailModal } from './BookingModals'
import { useBusiness } from './Layout'

export function GuestsPage() {
  const { business, can } = useBusiness()
  const [q, setQ] = useState('')
  const query = useDebounced(q)
  const [openId, setOpenId] = useState<string | null>(null)
  const list = useLoad(() => get<{ guests: Guest[] }>(`/v1/admin/businesses/${business.id}/guests?limit=100${query ? `&q=${encodeURIComponent(query)}` : ''}`), [business.id, query])

  return (
    <>
      <div className="topbar">
        <h1>Guests</h1>
      </div>
      <div className="search" style={{ marginBottom: 14 }}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <circle cx="11" cy="11" r="7" />
          <path d="m20 20-3.5-3.5" />
        </svg>
        <Input placeholder="Search by name, phone or email" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search guests" />
      </div>
      {list.error ? <Alert>{list.error}</Alert> : null}
      {list.loading && !list.data ? <Spinner /> : null}
      {list.data && list.data.guests.length === 0 ? <Empty title="No guests yet">Guests are added automatically with their first booking.</Empty> : null}
      {list.data && list.data.guests.length > 0 ? (
        <div className="card table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Contact</th>
                <th>Visits</th>
                <th>No-shows</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {list.data.guests.map((g) => (
                <tr key={g.id} className="clickable" onClick={() => setOpenId(g.id)} tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && setOpenId(g.id)}>
                  <td style={{ fontWeight: 600 }}>
                    {g.name} {g.blocked ? <span className="chip chip-no_show">Blocked</span> : null}
                  </td>
                  <td className="muted small">
                    {g.phone ?? ''}
                    {g.phone && g.email ? ' · ' : ''}
                    {g.email ?? ''}
                  </td>
                  <td>{g.visitCount}</td>
                  <td>{g.noShowCount ? <span style={{ color: 'var(--danger)', fontWeight: 600 }}>{g.noShowCount}</span> : 0}</td>
                  <td className="muted small">{g.notes ? 'Has notes' : ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
      {openId ? <GuestModal guestId={openId} canEdit={can('manager')} onClose={() => setOpenId(null)} onChanged={() => void list.reload({ quiet: true })} /> : null}
    </>
  )
}

function GuestModal({ guestId, canEdit, onClose, onChanged }: { guestId: string; canEdit: boolean; onClose: () => void; onChanged: () => void }) {
  const { business } = useBusiness()
  const detail = useLoad(() => get<{ guest: Guest; bookings: Booking[] }>(`/v1/admin/businesses/${business.id}/guests/${guestId}`), [business.id, guestId])
  const [notes, setNotes] = useState<string | null>(null)
  const [blockReason, setBlockReason] = useState('')
  const [openBooking, setOpenBooking] = useState<Booking | null>(null)
  const save = useAction(async (body: { notes?: string | null; blocked?: boolean; blockedReason?: string | null }) => {
    await patch(`/v1/admin/businesses/${business.id}/guests/${guestId}`, body)
    await detail.reload({ quiet: true })
    onChanged()
  })
  const g = detail.data?.guest
  const currentNotes = notes ?? g?.notes ?? ''
  return (
    <Modal title={g?.name ?? 'Guest'} onClose={onClose} wide>
      {detail.loading && !detail.data ? <Spinner /> : null}
      {detail.error ? <Alert>{detail.error}</Alert> : null}
      {g ? (
        <div className="stack-lg">
          <dl className="detail-grid">
            <dt>Phone</dt>
            <dd>{g.phone ? <a href={`tel:${g.phone}`}>{g.phone}</a> : '—'}</dd>
            <dt>Email</dt>
            <dd>{g.email ? <a href={`mailto:${g.email}`}>{g.email}</a> : '—'}</dd>
            <dt>Visits</dt>
            <dd>
              {g.visitCount} completed, {g.noShowCount} no-show{g.noShowCount === 1 ? '' : 's'}
            </dd>
            <dt>Marketing</dt>
            <dd>{g.marketingConsent ? 'Opted in' : 'Not opted in'}</dd>
          </dl>
          {g.blocked ? (
            <div className="note-box">
              Blocked from booking online{g.blockedReason ? `: ${g.blockedReason}` : ''}.{' '}
              {canEdit ? (
                <button className="link-btn" onClick={() => void save.run({ blocked: false, blockedReason: null })}>
                  Unblock
                </button>
              ) : null}
            </div>
          ) : null}
          {save.error ? <Alert>{save.error}</Alert> : null}
          <Field label="Notes (staff only)" hint="Allergies, seating preferences, how they like to be looked after.">
            {(id) => <Textarea id={id} value={currentNotes} onChange={(e) => setNotes(e.target.value)} disabled={!canEdit} />}
          </Field>
          {canEdit && notes !== null && notes !== (g.notes ?? '') ? (
            <div className="row-flex">
              <Button variant="primary" busy={save.busy} onClick={() => void save.run({ notes: notes.trim() || null }).then(() => setNotes(null))}>
                Save notes
              </Button>
            </div>
          ) : null}
          <section className="stack">
            <h3>Booking history</h3>
            {detail.data?.bookings.length ? (
              <div className="booking-list">
                {detail.data.bookings.map((b) => (
                  <div key={b.id}>
                    <div className="muted small" style={{ margin: '0 0 4px 4px' }}>
                      {b.date}
                    </div>
                    <BookingRow booking={b} onOpen={() => setOpenBooking(b)} />
                  </div>
                ))}
              </div>
            ) : (
              <p className="muted small">No bookings yet.</p>
            )}
          </section>
          {canEdit && !g.blocked ? (
            <details>
              <summary className="small muted" style={{ cursor: 'pointer' }}>
                Block this guest from booking online
              </summary>
              <div className="stack" style={{ marginTop: 10 }}>
                <Field label="Reason (staff only)">{(id) => <Input id={id} value={blockReason} onChange={(e) => setBlockReason(e.target.value)} />}</Field>
                <div className="row-flex">
                  <Button variant="danger" busy={save.busy} onClick={() => void save.run({ blocked: true, blockedReason: blockReason || null })}>
                    Block guest
                  </Button>
                </div>
              </div>
            </details>
          ) : null}
        </div>
      ) : null}
      {openBooking ? (
        <BookingDetailModal
          booking={openBooking}
          onClose={() => setOpenBooking(null)}
          onChange={(b) => {
            setOpenBooking(b)
            void detail.reload({ quiet: true })
          }}
        />
      ) : null}
    </Modal>
  )
}
